import React, { useState, useEffect } from 'react';
import { instance } from "../../../api/axiosInstance";

interface EmailVerificationFormProps {
  onEmailVerify: (email: string) => void;
}

const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({ onEmailVerify }) => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [timer, setTimer] = useState(60); // 타이머 초기값 (1분: 60초)
  const [isResendEnabled, setIsResendEnabled] = useState(false);

  // 이메일 유효성 검사 함수
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 타이머 감소 효과
  useEffect(() => {
    let countdown: NodeJS.Timeout;

    if (isCodeSent && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendEnabled(true); // 타이머가 끝나면 재발송 가능
    }

    return () => clearInterval(countdown);
  }, [isCodeSent, timer]);

  // 타이머 포맷팅 함수 (mm:ss 형식)
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 이메일 인증 코드 발송 함수
  const handleSendVerificationCode = async () => {
    setError(null);
    setSuccessMessage(null);

    // 이메일 유효성 검사를 추가
    if (!isValidEmail(email)) {
      setError('유효한 이메일 주소를 입력해주세요.');
      return;
    }

    try {
      const response = await instance.post('/api/send-verification', { email });
      setIsCodeSent(true);
      setSuccessMessage(response.data.message || '인증번호가 이메일로 전송되었습니다.');
      setTimer(600); // 타이머 초기화 (1분)
      setIsResendEnabled(false); // 타이머 재설정 시 재발송 비활성화
    } catch (err: any) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || '이미 등록된 이메일입니다.');
      } else {
        setError('인증 코드 발송에 실패했습니다.');
      }
    }
  };

  // 인증 코드 확인 함수
  const handleVerifyCode = async () => {
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await instance.post('/api/verify-code', { email, code: verificationCode });
      setSuccessMessage(response.data.message || '인증이 완료되었습니다.');
      onEmailVerify(email); // 인증 완료 시 부모에 이메일 전달
    } catch (err: any) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || '인증에 실패했습니다. 인증번호를 확인해주세요.');
      } else {
        setError('인증 코드 확인에 실패했습니다.');
      }
    }
  };

  return (
    <div className='w-[400px] '>
      {!isCodeSent ? (
        <div className='flex flex-col  box-border '>
          <h2 className="text-2xl text-black font-bold mb-2">이메일 인증</h2>
          <p className="text-[#1a1a1a80] leading-6 text-sm font-light mb-6">이메일 주소를 입력하여 인증 코드를 발송받으세요.</p>
          <input
            type="email"
            placeholder="이메일을 입력하세요"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mobile:w-[315px] p-2 mb-6 border rounded-lg h-[50px]"
          />
          {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
          {successMessage && <p className="text-green-500 text-sm mb-2">{successMessage}</p>}
          <button
            onClick={handleSendVerificationCode}
            className={`font-bold text-base mobile:text-[13px] tracking-wide transition duration-300 ease-in-out text-white py-3 mobile:py-2 w-[150px] mobile:w-[100px] rounded ${email ? 'bg-MainColor hover:bg-opacity-80' : 'bg-gray-400 cursor-not-allowed'
              }`}
            disabled={!email} // 이메일이 입력되지 않으면 버튼 비활성화
          >
            인증코드 발송
          </button>
        </div>
      ) : (
        <div className='w-[400px]'>
          <h2 className="text-2xl font-bold mb-2">인증 코드 확인</h2>
          <p className="text-[#555] text-sm font-normal mb-6">이메일로 전송된 인증코드를 입력해주세요.</p>
          <input
            type="text"
            placeholder="인증 코드를 입력하세요"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            className="w-full p-2 mb-4 border bg-transparent rounded-lg h-[50px]"
          />
          {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
          {successMessage && <p className="text-green-500 text-sm mb-2">{successMessage}</p>}

          {/* 타이머 종료 시 확인 버튼 대신 재전송 버튼 표시 */}
          {timer === 0 ? (
            <button
              onClick={handleSendVerificationCode}
              className="font-bold text-base tracking-wide transition duration-300 ease-in-out text-white p-3 w-[150px] rounded bg-MainColor hover:bg-opacity-80"
            >
              재전송
            </button>
          ) : (
            <button
              onClick={handleVerifyCode}
              className={`font-bold text-base tracking-wide transition duration-300 ease-in-out text-white p-3 w-[150px] rounded ${verificationCode ? 'bg-MainColor hover:bg-opacity-80' : 'bg-gray-400 cursor-not-allowed'
                }`}
              disabled={!verificationCode} // 인증코드가 입력되지 않으면 버튼 비활성화
            >
              확인
            </button>
          )}

          {/* 타이머 표시 */}
          {timer > 0 && (
            <div className='absolute right-9 top-[39%]'>
              <p className="mt-4 text-base z-20 font-bold text-[#393939]">{formatTime(timer)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailVerificationForm;
